import { useEffect, useRef } from 'react';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { signOut } from 'aws-amplify/auth';

export const DCF62_INACTIVITY_TIMEOUT_MS = 60 * 60 * 1000;
export const DCF62_IDLE_SIGN_OUT_RETRY_MS = 5000;
export const DCF62_IDLE_SIGN_OUT_ATTEMPT_TIMEOUT_MS = 15000;

const ACTIVITY_EVENTS = [
  'keydown',
  'mousedown',
  'mousemove',
  'pointerdown',
  'scroll',
  'touchstart',
  'wheel',
];

const ACTIVITY_EVENT_OPTIONS = { passive: true, capture: true };

const withSignOutAttemptBound = (signOutResult) => {
  let timeoutId = null;
  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = window.setTimeout(() => {
      reject(new Error('Idle sign-out did not settle'));
    }, DCF62_IDLE_SIGN_OUT_ATTEMPT_TIMEOUT_MS);
  });

  return Promise.race([Promise.resolve(signOutResult), timeoutPromise]).finally(() => {
    if (timeoutId !== null) {
      window.clearTimeout(timeoutId);
    }
  });
};

export const SessionTimeoutGuard = () => {
  const { authStatus } = useAuthenticator((context) => [context.authStatus]);
  const timeoutRef = useRef(null);
  const signedOutRef = useRef(false);
  const signOutInFlightRef = useRef(false);

  useEffect(() => {
    if (authStatus !== 'authenticated') {
      return undefined;
    }

    let isDisposed = false;

    const clearLogoutTimer = () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };

    const scheduleSignOut = (delayMs) => {
      clearLogoutTimer();
      timeoutRef.current = window.setTimeout(() => {
        if (signOutInFlightRef.current) {
          return;
        }

        signedOutRef.current = true;
        signOutInFlightRef.current = true;
        void Promise.resolve()
          .then(() => withSignOutAttemptBound(signOut()))
          .then(() => {
            window.location.href = '/login';
          })
          .catch(() => {
            if (isDisposed) {
              return;
            }

            scheduleSignOut(DCF62_IDLE_SIGN_OUT_RETRY_MS);
          })
          .finally(() => {
            signOutInFlightRef.current = false;
          });
      }, delayMs);
    };

    const armLogoutTimer = () => {
      scheduleSignOut(DCF62_INACTIVITY_TIMEOUT_MS);
    };

    const recordActivity = () => {
      if (signedOutRef.current) {
        return;
      }

      armLogoutTimer();
    };

    signedOutRef.current = false;
    signOutInFlightRef.current = false;
    armLogoutTimer();
    for (const eventName of ACTIVITY_EVENTS) {
      window.addEventListener(eventName, recordActivity, ACTIVITY_EVENT_OPTIONS);
    }

    return () => {
      isDisposed = true;
      clearLogoutTimer();
      for (const eventName of ACTIVITY_EVENTS) {
        window.removeEventListener(eventName, recordActivity, ACTIVITY_EVENT_OPTIONS);
      }
    };
  }, [authStatus]);

  return null;
};

export default SessionTimeoutGuard;
